import React, { useMemo, useState } from 'react';
import PlayerProfile from './PlayerProfile';

const RosterDisplay = ({ roster }) => {
  const [activeGroup, setActiveGroup] = useState('Pitchers');
  const [selectedPlayer, setSelectedPlayer] = useState(null);

  const groupedRoster = useMemo(() => {
    const groups = { 'Pitchers': [], 'Catchers': [], 'Infielders': [], 'Outfielders': [] };
    if (roster?.length > 0) {
      roster.forEach(player => {
        const posType = player?.position?.type;
        if (posType === 'Pitcher') groups['Pitchers'].push(player);
        else if (posType === 'Catcher') groups['Catchers'].push(player);
        else if (posType === 'Infielder') groups['Infielders'].push(player);
        else if (posType === 'Outfielder') groups['Outfielders'].push(player);
      });
    }
    return groups;
  }, [roster]);

  if (!roster || roster.length === 0) return null;

  const PlayerRow = ({ player }) => (
    <div
      onClick={() => setSelectedPlayer(player)}
      className="flex items-center justify-between py-1.5 border-b border-[rgba(101,141,113,0.3)] last:border-0 hover:bg-[rgba(216,206,179,0.1)] px-2 transition-colors group cursor-pointer"
    >
      <div className="flex items-center gap-2">
        <span className="text-[10px] font-bold tabular-nums text-[#d8ceb3] w-5">
          {player.jerseyNumber || '--'}
        </span>
        <span className="text-xs sm:text-sm font-medium text-white uppercase tracking-wide group-hover:text-[#d8ceb3] transition-colors">
          {player.person.fullName}
        </span>
      </div>
      <div className="flex items-center gap-2">
        <span className="text-[10px] font-bold text-[#a9c9b1] uppercase">
          {player.position.abbreviation}
        </span>
        <span className="text-[9px] text-[#658d71] group-hover:text-[#a9c9b1] transition-colors">›</span>
      </div>
    </div>
  );

  return (
    <>
      <div className="border-2 border-[#658d71] p-3 sm:p-6 bg-[rgba(0,0,0,0.05)]">
        <h3 className="text-base sm:text-xl font-bold tracking-widest text-[#d8ceb3] mb-4 uppercase flex items-center gap-2">
          Active Roster
          <span className="text-[#a9c9b1] text-xs font-medium tracking-normal lowercase">({roster.length})</span>
          <span className="ml-auto text-[9px] font-medium text-[#658d71] normal-case tracking-normal">Click a player for profile</span>
        </h3>

        {/* Mobile Tab Switcher */}
        <div className="flex sm:hidden gap-1 mb-4 overflow-x-auto pb-1">
          {Object.keys(groupedRoster).map(group => (
            <button
              key={group}
              onClick={() => setActiveGroup(group)}
              className={`shrink-0 text-[10px] font-bold uppercase tracking-wider px-3 py-1.5 border transition-colors ${activeGroup === group ? 'bg-[#658d71] border-[#8aa890] text-white' : 'border-[#658d71] text-[#a9c9b1] hover:text-white'}`}
            >
              {group} ({groupedRoster[group].length})
            </button>
          ))}
        </div>

        {/* Mobile: Single Group View */}
        <div className="sm:hidden">
          <div className="flex flex-col">
            {groupedRoster[activeGroup]
              .sort((a, b) => (a.jerseyNumber || 99) - (b.jerseyNumber || 99))
              .map(player => <PlayerRow key={player.person.id} player={player} />)}
          </div>
        </div>

        {/* Desktop: Full Grid */}
        <div className="hidden sm:grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {Object.entries(groupedRoster).map(([groupName, players]) => (
            <div key={groupName} className="flex flex-col">
              <h4 className="text-[#a9c9b1] text-xs font-bold tracking-[0.2em] uppercase mb-3 pb-1 border-b-2 border-[#658d71]">
                {groupName}
              </h4>
              <div className="flex flex-col">
                {players.length > 0 ? (
                  players
                    .sort((a, b) => (a.jerseyNumber || 99) - (b.jerseyNumber || 99))
                    .map(player => <PlayerRow key={player.person.id} player={player} />)
                ) : (
                  <span className="text-[10px] italic text-[#658d71] py-2">No players listed</span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {selectedPlayer && (
        <PlayerProfile
          playerId={selectedPlayer.person.id}
          playerName={selectedPlayer.person.fullName}
          onClose={() => setSelectedPlayer(null)}
        />
      )}
    </>
  );
};

export default RosterDisplay;